import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Card, Chip, Hairline, Icon, RubberStamp, Section, Swatch, Tape } from "@/components/primitives";
import type { SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { EvidenceChecklist } from "@/components/EvidenceChecklist";
import { ListingStatusSheet } from "@/components/ListingStatusSheet";
import { ListingBadge } from "@/components/ListingBadge";
import { VerdictFeedback } from "@/components/VerdictFeedback";
import { PrepareListing } from "@/components/PrepareListing";
import { ScanReviewActions } from "@/components/ScanReviewActions";
import { parseMarketplaceLinks, type MarketplaceLink } from "@/lib/marketplaces";
import { useTierFeatures } from "@/hooks/useTierFeatures";
import { TierLockedCard } from "@/components/TierLockedCard";
import { ScanPdfExportButton } from "@/components/ScanPdfExportButton";
import { PriceCorrectionDialog } from "@/components/PriceCorrectionDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMyProfile } from "@/hooks/useFigData";
import { mergedItem, scanStore } from "@/lib/scanStore";
import type { Json } from "@/integrations/supabase/types";
import type { FigVerifyResult } from "@/lib/scanStore";
import { useBackgroundScans } from "@/hooks/useBackgroundScans";
import { notify, tap } from "@/lib/nativeHaptics";
import { ResultPending } from "@/components/ResultPending";

// ─── FIG Verify display helpers ─────────────────────────────────────────
// All visual tokens come from index.css / tailwind.config.ts so the look
// stays inside the existing FIG palette. No raw color values here.
type VerdictKey =
  | "authentic_vintage"
  | "likely_vintage"
  | "inconclusive"
  | "unlikely_vintage"
  | "not_vintage"
  | "insufficient_evidence"
  | "refer_to_human";

const VERDICT_DISPLAY: Record<VerdictKey, { label: string; bg: string; fg: string; sub: string }> = {
  authentic_vintage:   { label: "FIG Verified",       bg: "hsl(var(--olive))", fg: "hsl(var(--cream))", sub: "Authenticated vintage." },
  likely_vintage:      { label: "Likely Vintage",     bg: "hsl(var(--ink))",   fg: "hsl(var(--sand))",  sub: "Strong evidence — minor open questions." },
  inconclusive:        { label: "Inconclusive",       bg: "hsl(var(--paper-3))", fg: "hsl(var(--ink))", sub: "Mixed signals — review the evidence log." },
  unlikely_vintage:    { label: "Unlikely Vintage",   bg: "hsl(var(--rust))",  fg: "hsl(var(--cream))", sub: "Construction or label inconsistencies present." },
  not_vintage:         { label: "Not Vintage",        bg: "hsl(var(--rust))",  fg: "hsl(var(--cream))", sub: "Anachronistic elements across multiple signals." },
  insufficient_evidence: { label: "More Photos Needed", bg: "hsl(var(--paper-3))", fg: "hsl(var(--ink))", sub: "Resubmit with the full garment, the labels, and a construction close-up." },
  // Categories listed in Section 19 (Hermès, fine jewelry, watches, furs,
  // designer archival). FIG can't verify these from photos alone — surfaces
  // a friendly "we're not there yet" panel instead of forcing a verdict.
  refer_to_human:      { label: "Coming Soon",  bg: "hsl(var(--cream))", fg: "hsl(var(--ink))",  sub: "We can't verify this category yet — we're expanding our verification channels." },
};

// Verdicts that should hide the pricing + forensic detail sections (because
// FIG didn't actually produce a verdict — no comps, no signal scores).
const QUIET_VERDICTS: Set<VerdictKey> = new Set(["refer_to_human"]);

function isPass(score: unknown): boolean {
  if (typeof score === "number") return score >= 60;
  return false;
}

/** Pass/fail glyph using existing FIG accent + secondary tokens. */
const SignalIndicator = ({ pass }: { pass: boolean }) => (
  <span
    aria-label={pass ? "pass" : "fail"}
    style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 14,
      lineHeight: 1,
      color: pass ? "hsl(var(--olive))" : "hsl(var(--ink-2))",
    }}
  >
    {pass ? "✦" : "—"}
  </span>
);

type ShareChoice = "private" | "feed";
type VerificationTier = "photo_verified" | "self_reported" | "uncertain";
type VerificationCategory = "fur" | "leather" | "luxury" | "exotic";
interface VerificationBlock {
  category_flag: VerificationCategory;
  tier: VerificationTier;
  evidence: string[];
  prompts: string[];
}
const CAPTION_MAX = 140;

const FLAG_COPY: Record<VerificationCategory, { label: string; warn: string }> = {
  fur:     { label: "Real fur claim",      warn: "Fur authenticity affects price and ethics — shoppers care." },
  leather: { label: "Real leather claim",  warn: "Real-vs-faux leather is hard to tell from a single photo." },
  luxury:  { label: "Luxury house",        warn: "This brand is widely counterfeited — proof matters." },
  exotic:  { label: "Exotic skin claim",   warn: "Exotic-skin items have legal and ethical sensitivities." },
};

const TIER_COPY: Record<VerificationTier, { label: string; sub: string; tone: "ink" | "rust" | "olive" }> = {
  photo_verified: { label: "Photo-verified",  sub: "Fig confirmed from your photos.", tone: "olive" },
  self_reported:  { label: "Self-reported",   sub: "Fig couldn't confirm — sold on your word.", tone: "rust" },
  uncertain:      { label: "Honestly unsure", sub: "You're not sure either — celebrated by good shoppers.", tone: "ink" },
};

export default function Result() {
  // Reusable collapsible header for the Forensic / Verification / Pricing
  // sections. Sits inside its own Section to mirror the existing rhythm.
  const SectionToggle = ({ label, open, onClick }: { label: string; open: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      aria-expanded={open}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: open ? "hsl(var(--ink))" : "hsl(var(--cream))",
        color:      open ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
        border: "1px solid hsl(var(--ink))",
        padding: "10px 14px", cursor: "pointer",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
        letterSpacing: "0.12em", textTransform: "uppercase",
      }}
    >
      <span>{label}</span>
      {Icon.chev(12, open ? "u" : "d")}
    </button>
  );

  const nav = useNavigate();
  const { state: locationState } = useLocation();
  const [searchParams] = useSearchParams();
  // Back chevron target. When callers pass { state: { from: "/some/path" } }
  // (e.g. clicking an item from the profile page), back returns there.
  // Otherwise default to /home — preserves the prior fix that prevented
  // the back chevron from kicking the user back into the scan flow.
  const backTarget = (locationState as { from?: string } | null)?.from ?? "/home";
  const requestedScanId = searchParams.get("scanId");
  const actionedRef = useRef(false);
  const savedScanIdRef = useRef<string | null>(null);
  const [savedScanId, setSavedScanId] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showPriceCorrection, setShowPriceCorrection] = useState(false);
  const { refreshUnviewed, registerScan } = useBackgroundScans();
  // Use refs so the hydration effect doesn't re-run when these callbacks
  // change reference (which would cancel the in-flight fetch and hang the spinner).
  const refreshUnviewedRef = useRef(refreshUnviewed);
  const registerScanRef = useRef(registerScan);
  useEffect(() => { refreshUnviewedRef.current = refreshUnviewed; }, [refreshUnviewed]);
  useEffect(() => { registerScanRef.current = registerScan; }, [registerScan]);

  // Auth must be resolved before we query — useAuth.loading starts true.
  const { user, loading: authLoading } = useAuth();

  // Resolve scan ID from URL param → nav state → sessionStorage (in priority order).
  const navStateScanId = (locationState as { scanId?: string } | null)?.scanId ?? null;
  const initialScanId = requestedScanId ?? navStateScanId ?? sessionStorage.getItem("fig_current_scan_id");

  const [hydrating, setHydrating] = useState<boolean>(!!initialScanId);
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  const [item, setItem] = useState(mergedItem(null));
  const [photos, setPhotos] = useState<string[]>([]);

  // ── Pending state ─────────────────────────────────────────────────────────
  // True while the scan row exists with processing_status === "processing"
  // and we're waiting for fig-verify to write the result. Replaces the old
  // "Reading the threads…" full-screen loader (Analyze.tsx). Renders the
  // user's photos + cycling status + skeleton sections via ResultPending.
  //
  // `wasPending` flips true the moment we transition pending → loaded so
  // we can fire a single haptic tap + apply a fade-in animation to the
  // newly-arrived result content.
  const [pending, setPending] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<string[]>([]);
  const [wasPending, setWasPending] = useState(false);
  const [activeScanId, setActiveScanId] = useState<string | null>(initialScanId);
  // Staged cascade: 0 = all skeletons; 1 = verdict revealed; 2 = + summary;
  // 3 = + signals; 4 = + pricing → unmount ResultPending, render full Result.
  // Starts at 0 while pending; bumped via timed setTimeouts after the
  // result blob arrives so each section appears with measurable spacing.
  const [revealStage, setRevealStage] = useState(0);
  // Cached verify result while we're cascading — passed to ResultPending so
  // it can render real content as each stage hits its threshold.
  const [cascadingResult, setCascadingResult] = useState<FigVerifyResult | null>(null);

  // Always fetch from DB on mount so we display the authoritative result.
  // Wait for auth to resolve first so the JWT is attached to the request.
  useEffect(() => {
    if (authLoading) return;
    const navId = (locationState as { scanId?: string } | null)?.scanId ?? null;
    const id = requestedScanId ?? navId ?? sessionStorage.getItem("fig_current_scan_id");
    if (!id) { setHydrating(false); return; }

    let cancelled = false;
    (async () => {
      if (cancelled) { setHydrating(false); return; }

      const { data, error } = await supabase
        .from("scans")
        .select("id, created_at, result, processing_status, error_message, photos, category, verification, bulk_job_id, title, brand, era, verdict, verdict_label, confidence, rarity, price_low, price_high, price_suggested, tags, notes, condition, condition_notes")
        .eq("id", id)
        .maybeSingle();

      if (cancelled) { setHydrating(false); return; }

      if (error || !data) {
        setHydrationError("fetch_failed");
        setHydrating(false);
        return;
      }

      const status = data.processing_status as string | null;
      const bulkJobId = (data as Record<string, unknown>).bulk_job_id as string | null | undefined;

      if (status === "processing") {
        if (bulkJobId) {
          registerScanRef.current(data.id);
          setHydrating(false);
          return;
        }
        // Non-bulk scan in processing — either fig-verify is mid-flight
        // and the user got to /result before it finished, or fig-verify hit a
        // runtime timeout (>60s) so the server-side safety net never fired
        // and the row will never self-heal. Check the row age: if it's been
        // sitting > 2 minutes we mark it failed client-side and surface a
        // real error + retry path. If still fresh, show the new pending UI
        // (photos + cycling status + skeletons) and subscribe to row updates
        // via Realtime so the result lights up the moment fig-verify writes.
        const createdAt = data.created_at as string | undefined;
        const ageMs = createdAt ? Date.now() - new Date(createdAt).getTime() : 0;
        if (ageMs > 120_000) {
          // Stuck — mark failed so it stops haunting the closet.
          try {
            await supabase
              .from("scans")
              .update({
                processing_status: "failed",
                error_message: "Verification didn't complete — your credit was not used. Try scanning again.",
                completed_at: new Date().toISOString(),
              })
              .eq("id", data.id);
          } catch (e) {
            console.warn("client-side stuck-scan rescue failed:", e);
          }
          setHydrationError("Verification didn't complete — your credit was not used. Try scanning again.");
          setHydrating(false);
          return;
        }
        // Fresh — enter pending state. Photos from the DB row drive the
        // visible portion of ResultPending; the Realtime effect below
        // watches for the row's `result` column to populate and triggers
        // a re-hydration to swap pending → live result.
        const dbPhotos = Array.isArray(data.photos)
          ? (data.photos as string[]).filter(Boolean)
          : [];
        setPendingPhotos(dbPhotos);
        setActiveScanId(data.id);
        setPending(true);
        setHydrating(false);
        return;
      }
      if (status === "failed") {
        setHydrationError(data.error_message || "Verification failed");
        setHydrating(false);
        return;
      }

      const raw = data.result;
      const verify = (raw == null
        ? null
        : typeof raw === "string"
          ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
          : raw) as FigVerifyResult | null;
      if (!verify) {
        // result column is null — the edge function hasn't written it yet or failed.
        setHydrationError("result_null");
        setHydrating(false);
        return;
      }

      // Hydrate scanStore so all sections that read from it (verdict, forensic,
      // pricing) get the authoritative data.
      scanStore.setVerifyResult(verify);
      try {
        const { figVerifyToAIScan } = await import("@/lib/figVerifyMap");
        // The DB row's `brand` already includes the userBrand fallback, so
        // passing it as the brand fallback ensures the closet/result title
        // includes the brand even when brand_detected was null (no-label).
        const ai = figVerifyToAIScan(verify, String((data as any).brand ?? ""));
        scanStore.setResult(ai);
        setItem(mergedItem(ai));
      } catch {
        // mapper failed — scanStore.getVerifyResult() still has the raw data
        // so verdict/forensic/pricing sections can still render
      }

      const dbPhotos = Array.isArray(data.photos) ? (data.photos as string[]).filter(Boolean) : [];
      if (dbPhotos.length) {
        scanStore.setPhotos(dbPhotos);
        setPhotos(dbPhotos);
      }

      sessionStorage.setItem("fig_scan_saved_id", data.id);
      sessionStorage.setItem("fig_current_scan_id", data.id);
      setSavedScanId(data.id);

      supabase
        .from("scans")
        .update({ viewed_at: new Date().toISOString() })
        .eq("id", data.id)
        .is("viewed_at", null)
        .then(() => { if (!cancelled) void refreshUnviewedRef.current(); });

      if (!cancelled) setHydrating(false);
    })();
    return () => { cancelled = true; };
  }, [requestedScanId, locationState, authLoading]);

  // Pending → loaded transition. Subscribes to the scan row via Supabase
  // Realtime while we're in pending state. When fig-verify writes the
  // result column (or flips processing_status to "complete"), we re-fetch
  // the row and flip out of pending. The Realtime channel also acts as a
  // safety net via a 3s polling backup in case the channel hasn't
  // delivered the row UPDATE event for whatever reason.
  useEffect(() => {
    if (!pending) return;
    if (!activeScanId) return;

    let cancelled = false;

    const rehydrate = async () => {
      const { data, error } = await supabase
        .from("scans")
        .select(
          "id, created_at, result, processing_status, error_message, photos, " +
          "category, verification, bulk_job_id, title, brand, era, verdict, " +
          "verdict_label, confidence, rarity, price_low, price_high, " +
          "price_suggested, tags, notes, condition, condition_notes",
        )
        .eq("id", activeScanId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const status = data.processing_status as string | null;
      const raw = data.result;
      if (status === "failed") {
        setHydrationError(data.error_message || "Verification failed");
        setPending(false);
        return;
      }
      if (raw == null) return; // still processing — Realtime / poll will fire again
      const verify = (typeof raw === "string"
        ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
        : raw) as FigVerifyResult | null;
      if (!verify) return;

      // Hydrate scanStore + photos + item the same way the initial
      // hydration effect does on a fresh load.
      scanStore.setVerifyResult(verify);
      try {
        const { figVerifyToAIScan } = await import("@/lib/figVerifyMap");
        const ai = figVerifyToAIScan(verify, String((data as Record<string, unknown>).brand ?? ""));
        scanStore.setResult(ai);
        if (!cancelled) setItem(mergedItem(ai));
      } catch {
        // mapper failed — scanStore still has raw data
      }
      const dbPhotos = Array.isArray(data.photos) ? (data.photos as string[]).filter(Boolean) : [];
      if (!cancelled) {
        if (dbPhotos.length) setPhotos(dbPhotos);
        setSavedScanId(activeScanId);
        savedScanIdRef.current = activeScanId;
        // Result has landed. Stay in pending state but pass the cascading
        // result to ResultPending. A separate effect (below) will advance
        // `revealStage` 1 → 2 → 3 → 4 with timing, and at stage 4 flip
        // pending=false to swap to the full Result render. Haptic fires at
        // stage 1 when the verdict pill appears, not now.
        setCascadingResult(verify);
        setWasPending(true);
      }
    };

    // Supabase Realtime channel for UPDATE events on this scan row.
    const channel = supabase
      .channel(`scan-${activeScanId}`)
      .on(
        "postgres_changes" as never,
        {
          event: "UPDATE",
          schema: "public",
          table: "scans",
          filter: `id=eq.${activeScanId}`,
        } as never,
        (payload: { new?: Record<string, unknown> }) => {
          const next = payload?.new;
          if (!next) return;
          const nextStatus = next.processing_status as string | null;
          // result populated OR status moved off processing → re-fetch.
          if (next.result != null || (nextStatus && nextStatus !== "processing")) {
            void rehydrate();
          }
        },
      )
      .subscribe();

    // Backup poll every 3s in case Realtime hasn't delivered yet.
    const pollT = setInterval(() => { void rehydrate(); }, 3_000);

    return () => {
      cancelled = true;
      clearInterval(pollT);
      supabase.removeChannel(channel);
    };
  }, [pending, activeScanId]);

  // Cascade driver — runs once `cascadingResult` is populated (fig-verify
  // just wrote the result blob). Advances revealStage 0 → 1 → 2 → 3 → 4
  // with stretched timing so each section's reveal is actually perceptible
  // (the prior 1.3s version landed as one chunk to the eye).
  //
  // Timing (each delta is from t=0 when cascadingResult lands):
  //   t = 0ms      → revealStage 1 (verdict pill + haptic tap)
  //   t = 700ms    → revealStage 2 (summary: era / brand / category / material)
  //   t = 1500ms   → revealStage 3 (signals observed)
  //   t = 2400ms   → revealStage 4 (pricing → flip pending=false, full Result mounts)
  //
  // Total cascade ~2.4s — enough time between stages that the eye registers
  // each arrival individually instead of perceiving the whole thing as one
  // event.
  useEffect(() => {
    if (!cascadingResult) return;
    setRevealStage(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => {
      setRevealStage(1);
      // Single light haptic at verdict reveal — mirrors notification arrival.
      void tap();
    }, 0));
    timers.push(setTimeout(() => setRevealStage(2), 700));
    timers.push(setTimeout(() => setRevealStage(3), 1500));
    timers.push(setTimeout(() => {
      setRevealStage(4);
      // Stage 4 fires the swap to the full Result render. The outer
      // container still gets fig-reveal via wasPending so the transition
      // feels intentional rather than a hard cut.
      setPending(false);
      setCascadingResult(null);
    }, 2400));
    return () => { for (const t of timers) clearTimeout(t); };
  }, [cascadingResult]);

  // Clear the wasPending flag after the reveal animation completes so
  // subsequent re-renders don't re-trigger the animation.
  useEffect(() => {
    if (!wasPending) return;
    const t = setTimeout(() => setWasPending(false), 600);
    return () => clearTimeout(t);
  }, [wasPending]);

  // Keep savedScanIdRef in sync so the discard confirmation can access it.
  useEffect(() => { savedScanIdRef.current = savedScanId; }, [savedScanId]);
  const { profile } = useMyProfile();
  const [showVerifyDetail, setShowVerifyDetail] = useState(false);
  // showPricingDetail state was here for the "How was this calculated?"
  // expandable, which is now removed. The full breakdown lives on /price.
  // Each report section is an independently collapsible dropdown. Default
  // resting state is all open; users can collapse/expand any section.
  type SectionKey = "forensic" | "pricing";
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    forensic: true, pricing: true,
  });
  const toggleSection = (k: SectionKey) =>
    setOpenSections((s) => ({ ...s, [k]: !s[k] }));
  // Which tab is currently visible. Each tab still contains its own
  // collapsible dropdown (managed via openSections above).
  const [activeTab, setActiveTab] = useState<SectionKey>("forensic");
  const [shareChoice, setShareChoice] = useState<ShareChoice>("private");
  const [caption, setCaption] = useState<string>("");
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  // Local mirror of marketplace_links for the in-page badge so edits made via
  // the sheet show up instantly. Seeded when the scan loads, kept in sync by
  // the sheet's onSaved callback.
  const [listingLinks, setListingLinks] = useState<MarketplaceLink[]>([]);
  const [listingSheetOpen, setListingSheetOpen] = useState(false);
  const [soldInfo, setSoldInfo] = useState<{ soldAt: string | null; soldPrice: number | null; soldOn: string | null }>({
    soldAt: null, soldPrice: null, soldOn: null,
  });
  // "Re-run with this hint" — surfaced once the user saves a disagree note.
  const [disagreeHint, setDisagreeHint] = useState<string>("");
  const [rerunning, setRerunning] = useState(false);

  const noLabelDeclared = useMemo(
    () => (scanStore.getHints() || "").includes("Submitter confirms no label present."),
    [],
  );
  const pass = item.verdict === "PASS";

  // Specialist-review verdict — Hermès, fine jewelry, watches, etc. Surfaced
  // as a friendly "we can't verify this brand quite yet" card; we hide the
  // forensic + pricing surfaces for it because there's no real verdict
  // (no signal scores) and no useful comp data (Hermès doesn't have
  // representative comps on eBay/Grailed).
  const verifyVerdictKey = (scanStore.getVerifyResult()?.assessment?.verdict as VerdictKey) ?? null;
  const isQuiet = verifyVerdictKey === "refer_to_human";

  // Fire haptic exactly once when the real scan finishes loading and verdict
  // is available. Guards against the FIG_ITEM placeholder (which has a baked-in
  // verdict) triggering the haptic before the actual scan resolves.
  const verdictAnnouncedRef = useRef(false);
  useEffect(() => {
    if (verdictAnnouncedRef.current) return;
    if (hydrating || hydrationError) return;
    if (!item.verdict) return;
    verdictAnnouncedRef.current = true;
    void notify(item.verdict === "PASS");
  }, [hydrating, hydrationError, item.verdict]);

  const { features } = useTierFeatures();
  const coverPhoto = photos[0] ?? null;
  // Photo viewer paging — supports swipe, arrow buttons, and keyboard ←/→.
  const photoScrollerRef = useRef<HTMLDivElement>(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const lightboxScrollerRef = useRef<HTMLDivElement>(null);
  const forensicSectionRef = useRef<HTMLDivElement>(null);
  const pricingSectionRef = useRef<HTMLDivElement>(null);
  const goToPhoto = (i: number) => {
    const el = photoScrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(photos.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };
  const goToLightbox = (i: number) => {
    const el = lightboxScrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(photos.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };
  useEffect(() => {
    if (photos.length <= 1) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (lightboxOpen) goToLightbox(activePhoto + 1);
        else goToPhoto(activePhoto + 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (lightboxOpen) goToLightbox(activePhoto - 1);
        else goToPhoto(activePhoto - 1);
      } else if (e.key === "Escape" && lightboxOpen) {
        setLightboxOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activePhoto, photos.length, lightboxOpen]);
  // When the lightbox opens, sync its scroll position to the active photo.
  useEffect(() => {
    if (!lightboxOpen) return;
    requestAnimationFrame(() => {
      const el = lightboxScrollerRef.current;
      if (el) el.scrollLeft = activePhoto * el.clientWidth;
    });
  }, [lightboxOpen]); // eslint-disable-line
  // Lock background scroll while the lightbox is open.
  useEffect(() => {
    if (!lightboxOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [lightboxOpen]);
  const analyzedCount = ("analyzedCount" in item) ? (item as { analyzedCount?: number }).analyzedCount ?? 1 : 1;
  const aiVerification = (item.verification ?? null) as VerificationBlock | null;

  // The collector can override Fig's tier downward (to "uncertain") — we never
  // let them upgrade past what the photos prove. State seeded from AI tier.
  const [verification, setVerification] = useState<VerificationBlock | null>(aiVerification);

  useEffect(() => {
    setVerification(aiVerification);
  }, [aiVerification]);

  // Fallback: persist scan to DB if hydration didn't find a pre-existing row
  // (e.g. demo mode, or old flow where Analyze.tsx didn't pre-create the row).
  useEffect(() => {
    if (!user || savedScanId) return; // hydration already handled it
    const aiResult = scanStore.getResult();
    if (!aiResult) return;
    const savedKey = "fig_scan_saved_id";
    const existing = sessionStorage.getItem(savedKey);
    if (existing) {
      sessionStorage.setItem("fig_current_scan_id", existing);
      setSavedScanId(existing);
      return;
    }
    (async () => {
      let geo: { lat: number; lng: number; city: string | null } | null = null;
      try {
        const raw = localStorage.getItem("fig.geo.cache");
        if (raw) {
          const parsed = JSON.parse(raw) as { coords: { lat: number; lng: number }; city: string | null };
          if (parsed?.coords) geo = { lat: parsed.coords.lat, lng: parsed.coords.lng, city: parsed.city ?? null };
        }
      } catch { /* no geo */ }

      const { data, error } = await supabase
        .from("scans")
        .insert({
          user_id: user.id,
          title: item.title,
          brand: item.brand,
          era: item.era,
          category: item.category,
          material: item.material,
          size: item.size,
          color: item.color,
          condition: item.condition,
          condition_notes: item.conditionNotes,
          verdict: item.verdict,
          verdict_label: item.verdictLabel,
          confidence: item.confidence,
          rarity: item.rarity,
          tags: item.tags ?? [],
          notes: item.notes ?? [],
          img: scanStore.getPhotos()[0] || "olive",
          photos: scanStore.getPhotos(),
          price_suggested: item.pricing.suggested,
          price_low: item.pricing.low,
          price_high: item.pricing.high,
          status: "kept",
          review_status: "approved_closet",
          lat: geo?.lat ?? null,
          lng: geo?.lng ?? null,
          city: geo?.city ?? null,
          verification: aiVerification as unknown as Json,
        })
        .select("id")
        .single();
      if (!error && data) {
        sessionStorage.setItem(savedKey, data.id);
        sessionStorage.setItem("fig_current_scan_id", data.id);
        setSavedScanId(data.id);
      }
    })();
  }, [user, savedScanId]);

  // Push the collector's chosen tier back to (a) the in-flight scan store so
  // ListForSale uses it for hedged AI copy, and (b) the persisted scan row.
  const setTier = async (next: VerificationTier) => {
    if (!verification) return;
    const updated: VerificationBlock = { ...verification, tier: next };
    setVerification(updated);
    // Update session-cached AI result so listing flow sees it.
    const cached = scanStore.getResult();
    if (cached) scanStore.setResult({ ...cached, verification: updated });
    if (savedScanId) {
      const { error } = await supabase
        .from("scans")
        .update({ verification: updated as unknown as Json })
        .eq("id", savedScanId);
      if (error) toast.error("Couldn't save that — try again");
      else if (next === "uncertain") toast.success("Saved as honestly unsure — shoppers respect this.");
      else toast.success("Verification updated");
    }
  };

  // Hydrate the badge with whatever marketplace_links are already on the row
  // (e.g. the collector came from /list and seeded one entry there). Runs each
  // time the saved id changes; runs once on initial load too.
  useEffect(() => {
    if (!savedScanId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("scans")
        .select("marketplace_links, posted, sold_at, sold_price, posted_to, status")
        .eq("id", savedScanId)
        .maybeSingle();
      if (cancelled || !data) return;
      setListingLinks(parseMarketplaceLinks((data as { marketplace_links?: unknown }).marketplace_links));
      if ((data as { posted?: boolean }).posted) setPosted(true);
      const d = data as {
        sold_at?: string | null; sold_price?: number | null; posted_to?: string | null; status?: string | null;
      };
      const soldFromStatus = d.status ? /sold/i.test(d.status) : false;
      setSoldInfo({
        soldAt: d.sold_at ?? (soldFromStatus ? new Date().toISOString() : null),
        soldPrice: d.sold_price ?? null,
        soldOn: d.posted_to ?? null,
      });
    })();
    return () => { cancelled = true; };
  }, [savedScanId]);

  // Default = private. Flips DB row off the public feed when (re)selected.
  const choose = async (choice: ShareChoice) => {
    setShareChoice(choice);
    if (choice === "private") {
      setPosted(false);
      if (!savedScanId) return;
      const { error } = await supabase
        .from("scans")
        .update({ posted: false, caption: null })
        .eq("id", savedScanId);
      if (error) toast.error("Couldn't save that — try again");
      else if (posted) toast("Removed from your feed");
    }
  };

  const confirmPost = async () => {
    if (posting) return;
    const trimmed = caption.trim().slice(0, CAPTION_MAX);
    if (!savedScanId) {
      setPosted(true);
      toast("Posted to your feed");
      return;
    }
    setPosting(true);
    const { error } = await supabase
      .from("scans")
      .update({ posted: true, caption: trimmed || null })
      .eq("id", savedScanId);
    setPosting(false);
    if (error) {
      toast.error("Couldn't post — try again");
      return;
    }
    setPosted(true);
    toast("Posted to your feed");
    supabase.functions.invoke("compute-grail-matches", {
      body: { kind: "scan", id: savedScanId },
    }).catch((e) => console.warn("semantic match failed:", e));
  };

  // Re-run analyze-garment using the user's correction note as an explicit
  // hint. Uses the photos already attached to the saved scan, then patches
  // the same scans row so history stays one row instead of duplicating.
  const rerunWithHint = async () => {
    if (!savedScanId || !disagreeHint.trim() || rerunning) return;
    const note = disagreeHint.trim().slice(0, 400);
    setRerunning(true);
    try {
      const { data: row } = await supabase
        .from("scans").select("photos, result").eq("id", savedScanId).maybeSingle();
      const imgs = (row?.photos as string[] | null)?.filter(Boolean) ?? scanStore.getPhotos();
      // Snapshot the current result so a failed invoke can restore the row
      // (we null it below to force the poll to wait for the NEW verdict).
      const oldResult = (row as Record<string, unknown> | null)?.result ?? null;
      if (imgs.length === 0) { toast.error("No photos to re-analyze."); setRerunning(false); return; }
      // fig-verify needs base64 image data, not URLs — fetch and encode each.
      const photos = await Promise.all(imgs.map(async (url, i) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const dataUrl: string = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = () => reject(r.error);
          r.readAsDataURL(blob);
        });
        const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl);
        return {
          label: `Photo ${i + 1}`,
          data: m ? m[2] : "",
          media_type: m ? m[1] : (blob.type || "image/jpeg"),
        };
      }));
      // Flip the row back into the processing state BEFORE invoking so the
      // page's own pending/poll/Realtime machinery re-engages and lights up
      // the NEW verdict the moment the background task writes it — exactly
      // like the initial-scan flow. We null `result` too so the poll waits
      // for the new verdict instead of re-cascading the stale one, and clear
      // error_message. We deliberately leave photos/category untouched — the
      // pending UI renders from them.
      const { error: procErr } = await supabase.from("scans")
        .update({ processing_status: "processing", error_message: null, result: null })
        .eq("id", savedScanId);
      if (procErr) throw new Error(procErr.message);

      // Prefix the hint with a strong signal so the prompt-side
      // userDetails block treats this as a verdict-corrective re-run
      // rather than just extra context.
      const { data, error } = await supabase.functions.invoke("fig-verify", {
        body: {
          photos,
          userDetails: `USER CORRECTION ON RERUN — the collector disagreed with the previous verdict on this same scan and asked us to re-evaluate with this hint: "${note}". Weight this heavily — re-examine the photos with this hypothesis in mind. If the hint is consistent with visible evidence, your re-evaluation should shift toward it. If contradicted, explain in your reasoning what specifically you observe that doesn't match.`,
          itemCategory: item.category || "",
          // Pass the existing scan id so fig-verify writes the re-verdict back
          // to this same row in its background task (returns HTTP 202 with
          // { status: "processing", scanId } — no verdict in the response).
          scanId: savedScanId,
        },
      });
      // Under the background contract the response body carries NO verdict —
      // a 202 { status: "processing" } means the re-verification was accepted.
      // Only a genuine invoke failure (or an explicit data.error) is fatal.
      if (error || (data as Record<string, any>)?.error) {
        // Restore the row out of processing (with its prior result) so it
        // isn't stranded in "processing" and the old report stays intact.
        await supabase.from("scans")
          .update({ processing_status: "complete", result: oldResult as unknown as Json })
          .eq("id", savedScanId);
        throw new Error(error?.message ?? (data as Record<string, any>)?.error ?? "Re-run failed");
      }

      // Accepted. The background task owns the row write now — do NOT write
      // any verdict from the response body. Reset this user's vote so they can
      // score the new verdict once it lands (independent of the new verdict).
      if (user) {
        await supabase.from("verdict_feedback")
          .delete().eq("scan_id", savedScanId).eq("user_id", user.id);
      }
      // Drop any stale in-memory result and re-enter the page's pending state.
      // The poll/Realtime effect (keyed on pending + activeScanId) fetches and
      // cascade-reveals the new verdict the moment the background write lands.
      scanStore.clearVerifyResult();
      scanStore.clearResult();
      setPendingPhotos(imgs);
      setActiveScanId(savedScanId);
      setCascadingResult(null);
      setRevealStage(0);
      setPending(true);
      setRerunning(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Re-run failed");
      setRerunning(false);
    }
  };

  if (hydrating) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", letterSpacing: "0.12em" }}>Loading…</div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 18, color: "hsl(var(--ink))", opacity: 0.6 }}>
            Retrieving your report
          </div>
        </div>
      </MobileFrame>
    );
  }

  // Pending / cascading — fig-verify is mid-flight OR has just landed and
  // the staged-reveal cascade is mid-flight. Once revealStage hits 4, the
  // driver effect above flips pending=false and the full Result render
  // takes over (with fig-reveal on the outer container via wasPending).
  if (pending) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh" }}>
          <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={() => nav(backTarget)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
              {Icon.chev(18, "l")}
            </button>
            <span className="caps" style={{ color: "hsl(var(--rust))" }}>Examining</span>
            <div style={{ width: 30 }} />
          </div>
          <ResultPending
            photos={pendingPhotos}
            revealStage={revealStage}
            result={cascadingResult}
          />
        </div>
      </MobileFrame>
    );
  }

  if (hydrationError) {
    const isSystemError = hydrationError !== "result_null" && hydrationError !== "fetch_failed";
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", padding: "60px 20px 40px" }}>
          <button onClick={() => nav(backTarget)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))", alignSelf: "flex-start" }}>
            {Icon.chev(18, "l")}
          </button>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center" }}>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: "hsl(var(--ink))", lineHeight: 1.2 }}>
              We couldn't load your result right now.
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 15, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              maxWidth: 320,
            }}>
              Your credit has not been used.
            </div>
            {isSystemError && (
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))", letterSpacing: "0.05em", maxWidth: 320 }}>
                {hydrationError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
              <button
                onClick={() => nav("/scan")}
                className="btn btn-primary"
                style={{ padding: "12px 22px" }}
              >
                Try again
              </button>
              <button
                onClick={() => nav("/home")}
                className="btn"
                style={{ padding: "12px 22px" }}
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      </MobileFrame>
    );
  }

  // ── Quick Thrift slim view ──────────────────────────────────────────────
  // When the scan was submitted via mode="quick", the result blob carries
  // meta.scan_mode === "quick" and a much smaller shape (brand/era/type +
  // rough price + buy_signal + one_liner). Render a minimal one-screen view
  // with the upsell to full authentication instead of the full forensic
  // surface.
  {
    const verifyResult = scanStore.getVerifyResult();
    const isQuick = (verifyResult as Record<string, unknown> | null)?.meta &&
      ((verifyResult as Record<string, Record<string, unknown>>).meta?.scan_mode === "quick");
    if (isQuick && verifyResult) {
      return (
        <QuickResultView
          result={verifyResult}
          photos={photos}
          item={item}
          onBack={() => nav(backTarget)}
        />
      );
    }
  }

  return (
    <MobileFrame>
      <div
        className={`paper-bg${wasPending ? " fig-reveal" : ""}`}
        style={{ minHeight: "100vh", paddingBottom: 40 }}
      >
        {/* ─── Top bar ─────────────────────────────────────────── */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(backTarget)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Report № {item.id}</span>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => nav("/share", {
                state: {
                  scanId: savedScanId,
                  photoUrl: coverPhoto,
                  title: item.title,
                  brand: item.brand,
                  era: item.era,
                  confidence: item.confidence,
                  priceLow: item.pricing.low,
                  priceHigh: item.pricing.high,
                  priceSuggested: item.pricing.suggested,
                },
              })}
              style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
            >
              {Icon.share(18)}
            </button>
          </div>
        </div>

        {/* ─── 1. VERDICT HERO ─────────────────────────────────── */}
        <Section pad={14}>
          {(() => {
            const verifyResult = scanStore.getVerifyResult();
            const verdictKey = ((verifyResult?.assessment?.verdict as string) ||
              (pass ? "likely_vintage" : "unlikely_vintage")) as VerdictKey;
            const v = VERDICT_DISPLAY[verdictKey] ?? VERDICT_DISPLAY.inconclusive;
            // v2: prefer the model-emitted user-facing strings over the
            // generic per-verdict sub-text. Priority order:
            //   1. low_confidence_caveat — only set when Consistent + Low,
            //      explains exactly which evidence was thin
            //   2. category_advisory — set when image-only auth has limits
            //      for the category (Hermès, fine jewelry, watches, etc.)
            //   3. fall back to the per-verdict generic sub
            // Numeric confidence percentage is intentionally NOT shown.
            const lowConfCaveat = verifyResult?.low_confidence_caveat ?? null;
            const categoryAdvisory = verifyResult?.category_advisory ?? null;
            const subText = lowConfCaveat || categoryAdvisory || v.sub;
            return (
              <div style={{
                background: v.bg, color: v.fg,
                padding: "16px 24px",
                border: "1px solid hsl(var(--ink) / 0.18)",
                borderRadius: 24,
                boxShadow: "0 1px 0 hsl(var(--ink) / 0.04), 0 12px 28px -18px hsl(var(--ink) / 0.25)",
              }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 600,
                  fontStyle: "italic",
                  fontSize: 36, lineHeight: 1.05, letterSpacing: "-0.015em",
                }}>
                  {v.label}
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 14, marginTop: 10, lineHeight: 1.5, opacity: 0.85,
                }}>
                  {subText}
                </div>
              </div>
            );
          })()}
        </Section>

        {/* ─── COMING-SOON action panel ─────────────────────────
              Replaces the forensic + pricing entry cards for categories
              we can't verify yet. Credit is already refunded server-side
              (fig-verify calls refund-scan after deriving the verdict).
              We surface that reassurance + a clean "what now" choice:
              re-run (in case the user thinks the categorization was wrong)
              or go home. */}
        {isQuiet && (
          <Section pad={14}>
            <div style={{
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
              padding: "14px 16px",
            }}>
              <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                Your credit is safe
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, lineHeight: 1.5, color: "hsl(var(--ink-2))",
              }}>
                We can't verify this category yet — we're working on expanding our verification channels. Your scan credit wasn't used. If you think this got the wrong category, try re-running.
              </div>
              <div style={{
                display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap",
              }}>
                <button
                  onClick={() => nav("/scan")}
                  className="btn btn-primary"
                  style={{ flex: 1, minWidth: 140, padding: "10px 14px", fontSize: 11 }}
                >
                  Try again
                </button>
                <button
                  onClick={() => nav("/home")}
                  className="btn"
                  style={{ flex: 1, minWidth: 140, padding: "10px 14px", fontSize: 11 }}
                >
                  Go home
                </button>
              </div>
            </div>
          </Section>
        )}

        {/* ─── 2. THE GARMENT — photo + title + meta ──────────── */}
        <Section pad={20} eyebrow="The Findings">
          {/* Photo strip */}
          {coverPhoto ? (
            <div className="fig-block" style={{
              border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3))",
              position: "relative", aspectRatio: "4/3", overflow: "hidden",
            }}>
              <div
                ref={photoScrollerRef}
                className="no-scrollbar"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const idx = Math.round(el.scrollLeft / el.clientWidth);
                  if (idx !== activePhoto) setActivePhoto(idx);
                }}
                style={{
                  display: "flex", width: "100%", height: "100%",
                  overflowX: "auto", overflowY: "hidden",
                  scrollSnapType: "x mandatory",
                  // pan-x pan-y lets vertical drags bubble to the page scroll;
                  // plain pan-x blocked page scrolling whenever the user
                  // touched the photo carousel.
                  touchAction: "pan-x pan-y",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehaviorX: "contain",
                }}
              >
                {photos.map((url, i) => (
                  <div
                    key={url + i}
                    style={{
                      flex: "0 0 100%", width: "100%", height: "100%",
                      scrollSnapAlign: "start",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => { setActivePhoto(i); setLightboxOpen(true); }}
                      aria-label={`Open photo ${i + 1} of ${photos.length} full screen`}
                      style={{
                        all: "unset", display: "block",
                        width: "100%", height: "100%", cursor: "zoom-in",
                      }}
                    >
                      <img
                        src={url}
                        alt={`${item.title} ${i + 1}/${photos.length}`}
                        draggable={false}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", userSelect: "none" }}
                      />
                    </button>
                  </div>
                ))}
              </div>
              {photos.length > 1 && (
                <>
                  <div style={{
                    position: "absolute", bottom: 8, left: 8,
                    background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                    padding: "3px 7px", fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                    pointerEvents: "none",
                  }}>
                    {activePhoto + 1} / {photos.length}
                  </div>
                  <button
                    type="button"
                    aria-label="Previous photo"
                    onClick={() => goToPhoto(activePhoto - 1)}
                    disabled={activePhoto === 0}
                    style={{
                      position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                      width: 36, height: 36, borderRadius: "50%",
                      border: "1px solid hsl(var(--ink))",
                      background: "hsl(var(--cream))", color: "hsl(var(--ink))",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: activePhoto === 0 ? "not-allowed" : "pointer",
                      opacity: activePhoto === 0 ? 0.4 : 1,
                    }}
                  >
                    {Icon.chev(14, "l")}
                  </button>
                  <button
                    type="button"
                    aria-label="Next photo"
                    onClick={() => goToPhoto(activePhoto + 1)}
                    disabled={activePhoto === photos.length - 1}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      width: 36, height: 36, borderRadius: "50%",
                      border: "1px solid hsl(var(--ink))",
                      background: "hsl(var(--cream))", color: "hsl(var(--ink))",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: activePhoto === photos.length - 1 ? "not-allowed" : "pointer",
                      opacity: activePhoto === photos.length - 1 ? 0.4 : 1,
                    }}
                  >
                    {Icon.chev(14, "r")}
                  </button>
                </>
              )}
              {analyzedCount > 1 && (
                <div style={{
                  position: "absolute", top: 8, right: 8,
                  display: "inline-flex", alignItems: "center", gap: 5,
                  background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
                  padding: "3px 8px", fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "hsl(var(--ink))",
                  pointerEvents: "none",
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "hsl(var(--rust))" }} />
                  {analyzedCount} photos analyzed
                </div>
              )}
            </div>
          ) : (
            <Swatch kind="olive" style={{ width: "100%", aspectRatio: "4/3", border: "1px solid hsl(var(--ink))" }} />
          )}

          {/* Title block */}
          <div style={{ marginTop: 14 }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 30,
              color: "hsl(var(--ink))", letterSpacing: "-0.02em", lineHeight: 1.05,
            }}>
              {item.title}
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15,
              color: "hsl(var(--ink-2))", marginTop: 4,
            }}>
              {[item.brand, item.era, item.origin].filter((s) => s && String(s).trim()).join(" · ")}
            </div>
          </div>
        </Section>

        {/* ─── 3. SUMMARY — at-a-glance answer ──────────────────── */}
        <Section pad={18} eyebrow="Summary">
          <Card style={{ padding: 0, borderRadius: 22, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              {([
                ["Category", item.category],
                ["Brand",    item.brand],
                ["Era",      item.era],
                ["Size",     item.size],
              ] as const).map(([k, v], i) => {
                const isLeft = i % 2 === 0;
                return (
                  <div key={k} style={{
                    padding: "10px 14px",
                    borderRight: isLeft ? "1px dashed hsl(var(--sand-deep))" : "none",
                    borderBottom: "1px dashed hsl(var(--sand-deep))",
                    minWidth: 0,
                  }}>
                    <div className="caps" style={{ marginBottom: 3 }}>{k}</div>
                    <div style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15,
                      color: "hsl(var(--ink))", lineHeight: 1.2,
                      overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {v || "—"}
                    </div>
                  </div>
                );
              })}
              {/* Material spans both columns — tier-defining detail. */}
              <div style={{
                gridColumn: "1 / -1",
                padding: "10px 14px",
                borderBottom: "1px dashed hsl(var(--sand-deep))",
                minWidth: 0,
              }}>
                <div className="caps" style={{ marginBottom: 3 }}>Material</div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15,
                  color: "hsl(var(--ink))", lineHeight: 1.2,
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {item.material || "—"}
                </div>
              </div>
              {/* Suggested price spans both columns — the takeaway. */}
              <div style={{
                gridColumn: "1 / -1",
                padding: "12px 14px",
                minWidth: 0,
              }}>
                <div className="caps" style={{ marginBottom: 3 }}>Suggested price</div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 22,
                  color: "hsl(var(--ink))",
                  lineHeight: 1.1,
                }}>
                  {item.pricing?.suggested ? `$${item.pricing.suggested}` : "—"}
                </div>
              </div>

              {/* Hand-verification advisory — fires on heavily-counterfeited
                  brands, pre-1975 designer pieces, or any item at $1000+. */}
              {(() => {
                const hv = (scanStore.getVerifyResult() as any)?.hand_verification;
                if (!hv?.advised) return null;
                const headlineByKind: Record<string, string> = {
                  counterfeit: "Verify authenticity in hand",
                  provenance:  "Verify provenance before listing",
                  high_value:  "Verify in hand before listing",
                };
                const headline = headlineByKind[String(hv.kind)] ?? "Verify in hand before listing";
                return (
                  <div style={{
                    gridColumn: "1 / -1",
                    padding: "12px 14px",
                    borderTop: "1px dashed hsl(var(--sand-deep))",
                    background: "hsl(40 70% 95%)",
                    minWidth: 0,
                  }}>
                    <div className="caps" style={{ marginBottom: 4, color: "hsl(28 65% 35%)" }}>
                      {headline}
                    </div>
                    <div style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif",
                      fontSize: 14,
                      color: "hsl(var(--ink))",
                      lineHeight: 1.35,
                    }}>
                      {String(hv.reason ?? "")}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Card>
          {/* Correct-this-price entry point. Only surfaces when FIG actually
              produced a suggested price (skips refer_to_human / blank pricing
              cases). Submits via submit_price_correction RPC. */}
          {savedScanId && item.pricing?.suggested ? (
            <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowPriceCorrection(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "hsl(var(--ink-2))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  cursor: "pointer",
                }}
              >
                Correct this price
              </button>
            </div>
          ) : null}
        </Section>

        {/* ─── 5. FORENSIC ─────────────────────────────────────── */}
        {!isQuiet && (
        <div ref={forensicSectionRef} style={{ scrollMarginTop: 0 }}>
        {(
          <Section pad={14}>
            <SectionToggle
              label="Forensic"
              open={openSections.forensic}
              onClick={() => toggleSection("forensic")}
            />
            {openSections.forensic && (
              <Card style={{ padding: 14, marginTop: 10 }}>
              {(() => {
                const vr = scanStore.getVerifyResult();
                const itemBlock = (vr?.item ?? {}) as Record<string, string | null | undefined>;
                const obs = (vr?.observations ?? {}) as Record<string, string | string[] | undefined>;
                const scores = (vr?.scores ?? {}) as Record<string, number | string | null | undefined>;
                const assessment = (vr?.assessment ?? {}) as Record<string, string | string[] | undefined>;

                // v2 educational fields — empty strings when reference was
                // silent on era/brand or the engine couldn't summarize.
                const eraContext = String(vr?.era_context ?? "");
                const brandContext = String(vr?.brand_context ?? "");
                const claimedEra = String(itemBlock.estimated_era ?? "");
                const claimedBrand = String(itemBlock.brand_detected ?? "");

                // v2 evidence — preferred over the legacy row-builder when
                // signals_observed has entries. Each signal carries a feature
                // observation, which image it came from, a tag, and (new) a
                // one-sentence rationale connecting the observation to its
                // meaning. We render the rationale inline so the result
                // teaches the user the WHY, not just the WHAT.
                const signals = Array.isArray(vr?.signals_observed) ? vr.signals_observed : [];
                // anachronisms + missing_signals are documented as string[] but
                // the model occasionally returns objects ({reason, signal} etc.)
                // — rendering that directly crashes React (#31). Coerce to a
                // best-effort string per entry: prefer `reason`/`description`/
                // `text`, fall back to JSON, drop empties. Keeps the surface
                // resilient to LLM schema drift without losing data.
                const coerceText = (entry: unknown): string => {
                  if (typeof entry === "string") return entry;
                  if (entry && typeof entry === "object") {
                    const o = entry as Record<string, unknown>;
                    const candidates = [o.reason, o.description, o.text, o.detail, o.note, o.signal];
                    for (const c of candidates) {
                      if (typeof c === "string" && c.trim()) return c;
                    }
                    try { return JSON.stringify(entry); } catch { return ""; }
                  }
                  return entry == null ? "" : String(entry);
                };
                const anachronisms = Array.isArray(vr?.anachronisms)
                  ? vr.anachronisms.map(coerceText).filter(Boolean)
                  : [];
                const missingSignals = Array.isArray(vr?.missing_signals)
                  ? vr.missing_signals.map(coerceText).filter(Boolean)
                  : [];

                const tagStyle = (tag: string): { color: string; label: string } => {
                  switch (tag) {
                    case "supports":     return { color: "hsl(var(--olive))", label: "Supports" };
                    case "contradicts":  return { color: "hsl(var(--rust))",  label: "Contradicts" };
                    case "missing":      return { color: "hsl(var(--ink-2))", label: "Not observable" };
                    case "inconclusive": return { color: "hsl(var(--ink-2))", label: "Inconclusive" };
                    default:             return { color: "hsl(var(--ink-2))", label: tag || "Signal" };
                  }
                };

                // Legacy row-builder — used when v2 signals aren't present
                // (older scans, or assessments where the model didn't emit
                // the new schema). Same evidence the page used to show.
                type Row = { name: string; text: string; pass: boolean };
                const legacyRows: Row[] = [];
                const labelText = String(obs.label_details ?? "");
                const originMatch = labelText.match(/(?:made in|country of origin|origin)[\s:]+([A-Z][A-Za-z .'-]{1,30})/i);
                const country = originMatch ? originMatch[1].trim() : "";
                const eraIndicators = Array.isArray(assessment.era_indicators)
                  ? (assessment.era_indicators as string[]).join(". ")
                  : "";
                const brandConf = String(itemBlock.brand_confidence || "").toLowerCase();

                if (noLabelDeclared) {
                  legacyRows.push({
                    name: "No label present",
                    text: "Assessed from construction and material only.",
                    pass: false,
                  });
                } else {
                  const labelNote = String(scores.label_note || "");
                  if (labelNote) legacyRows.push({
                    name: "Main label", text: labelNote,
                    pass: isPass(scores.label_authenticity),
                  });
                }
                const matDesc = String(itemBlock.material_description || "");
                if (matDesc && !/indeterminate|not assessed/i.test(matDesc)) {
                  legacyRows.push({ name: "Material", text: matDesc, pass: isPass(scores.material_consistency) });
                }
                const constNote = String(scores.construction_note || "");
                if (constNote) legacyRows.push({
                  name: "Construction era", text: constNote,
                  pass: isPass(scores.construction_era),
                });
                if (claimedBrand) legacyRows.push({
                  name: "Brand", text: claimedBrand,
                  pass: brandConf === "high" || brandConf === "medium",
                });
                if (country) legacyRows.push({ name: "Country of origin", text: country, pass: true });
                if (eraIndicators) legacyRows.push({ name: "Design details", text: eraIndicators, pass: true });

                const safeForensic = item.forensic ?? [];
                const useLegacy = signals.length === 0;
                const legacyFallback: Row[] = useLegacy && legacyRows.length === 0 && safeForensic.length > 0
                  ? safeForensic
                      .filter((f) => f.value && !/indeterminate|not assessed/i.test(f.value))
                      .map((f) => ({ name: f.label, text: f.value, pass: f.confidence >= 0.6 }))
                  : [];
                const finalLegacyRows = useLegacy ? (legacyRows.length > 0 ? legacyRows : legacyFallback) : [];

                return (
                  <>
                    {/* ── Educational context — era + brand ──────────────────── */}
                    {(eraContext || brandContext) && (
                      <div style={{ marginBottom: 14 }}>
                        {eraContext && (
                          <div style={{
                            padding: "12px 14px",
                            background: "hsl(var(--paper-3))",
                            border: "1px solid hsl(var(--ink) / 0.18)",
                            borderRadius: 12,
                            marginBottom: 8,
                          }}>
                            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 4 }}>
                              About the era{claimedEra ? ` · ${claimedEra}` : ""}
                            </div>
                            <div style={{
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 14, lineHeight: 1.45,
                              color: "hsl(var(--ink))",
                            }}>
                              {eraContext}
                            </div>
                          </div>
                        )}
                        {brandContext && (
                          <div style={{
                            padding: "12px 14px",
                            background: "hsl(var(--paper-3))",
                            border: "1px solid hsl(var(--ink) / 0.18)",
                            borderRadius: 12,
                          }}>
                            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 4 }}>
                              About the brand{claimedBrand ? ` · ${claimedBrand}` : ""}
                            </div>
                            <div style={{
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 14, lineHeight: 1.45,
                              color: "hsl(var(--ink))",
                            }}>
                              {brandContext}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Evidence log — v2 signals_observed (preferred) ─────── */}
                    {!useLegacy && (
                      <>
                        <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
                          Evidence log · {signals.length} signal{signals.length === 1 ? "" : "s"}
                        </div>
                        {signals.map((s, i) => {
                          const t = tagStyle(s.tag);
                          return (
                            <div key={`sig-${i}`} style={{
                              padding: "12px 0",
                              borderTop: i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                                <span style={{
                                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                                  fontWeight: 700, fontSize: 15,
                                  color: "hsl(var(--ink))",
                                  lineHeight: 1.3,
                                }}>
                                  {s.feature}
                                </span>
                                <span style={{
                                  flexShrink: 0,
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
                                  color: t.color, fontWeight: 700,
                                }}>
                                  {t.label}
                                </span>
                              </div>
                              {s.rationale && (
                                <div style={{
                                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                                  fontStyle: "italic",
                                  fontSize: 13, lineHeight: 1.45,
                                  color: "hsl(var(--ink-2))",
                                  marginTop: 6,
                                }}>
                                  {s.rationale}
                                </div>
                              )}
                              <div style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                                color: "hsl(var(--ink-2))",
                                marginTop: 8,
                                opacity: 0.7,
                              }}>
                                Photo {s.image_index}
                                {s.ref_section && s.ref_section !== "reference-silent" && !s.ref_section.startsWith("web:") && (
                                  <> · {s.ref_section}</>
                                )}
                                {s.ref_section === "reference-silent" && <> · reference silent</>}
                                {s.ref_section?.startsWith("web:") && <> · web finding</>}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* ── Legacy fallback (old scans only) ───────────────────── */}
                    {useLegacy && finalLegacyRows.length > 0 && (
                      <>
                        <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
                          Evidence log · {finalLegacyRows.length} signal{finalLegacyRows.length === 1 ? "" : "s"}
                        </div>
                        {finalLegacyRows.map((r, i) => (
                          <div key={r.name + i} style={{
                            padding: "10px 0",
                            borderTop: i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <span className="caps">{r.name}</span>
                              <SignalIndicator pass={r.pass} />
                            </div>
                            <div style={{
                              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
                              color: "hsl(var(--ink))", marginTop: 4, lineHeight: 1.4,
                            }}>
                              {r.text}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* ── Anachronisms — direct contradictions of the claim ─── */}
                    {anachronisms.length > 0 && (
                      <div style={{
                        marginTop: 14,
                        padding: "12px 14px",
                        background: "hsl(var(--rust) / 0.08)",
                        border: "1px solid hsl(var(--rust) / 0.3)",
                        borderRadius: 12,
                      }}>
                        <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                          Why this doesn't add up
                        </div>
                        {anachronisms.map((a, i) => (
                          <div key={i} style={{
                            fontFamily: "'Fraunces', 'Playfair Display', serif",
                            fontSize: 13, lineHeight: 1.45,
                            color: "hsl(var(--ink))",
                            marginTop: i > 0 ? 6 : 0,
                          }}>
                            — {a}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Missing signals — what would raise confidence ──────── */}
                    {missingSignals.length > 0 && (
                      <div style={{
                        marginTop: 12,
                        padding: "12px 14px",
                        background: "hsl(var(--paper-3))",
                        border: "1px dashed hsl(var(--ink) / 0.25)",
                        borderRadius: 12,
                      }}>
                        <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 6 }}>
                          What would raise confidence
                        </div>
                        {missingSignals.map((m, i) => (
                          <div key={i} style={{
                            fontFamily: "'Fraunces', 'Playfair Display', serif",
                            fontSize: 13, lineHeight: 1.45,
                            color: "hsl(var(--ink-2))",
                            marginTop: i > 0 ? 6 : 0,
                          }}>
                            — {m}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              {(item.notes ?? []).length > 0 && (
                <>
                  <Hairline dashed style={{ margin: "12px 0 8px" }} />
                  <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>The Story</div>
                  {(item.notes ?? []).map((n, i) => (
                    <div key={i} style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif",
                      fontSize: 14, color: "hsl(var(--ink-2))", marginBottom: 6, lineHeight: 1.35,
                    }}>
                      — {n}
                    </div>
                  ))}
                </>
              )}
              {(item.tags ?? []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                  {(item.tags ?? []).map((t) => <Chip key={t}>#{t}</Chip>)}
                </div>
              )}
              </Card>
            )}
          </Section>
        )}
        </div>
        )}

        {/* ─── 5b. PRICING ─────────────────────────────────────── */}
        {!isQuiet && (
        <div ref={pricingSectionRef} style={{ scrollMarginTop: 0 }}>
        {(
          <Section pad={14}>
            <SectionToggle
              label="Pricing"
              open={openSections.pricing}
              onClick={() => toggleSection("pricing")}
            />
            {openSections.pricing && (
              !features.canSeePricing ? (
                <div style={{ marginTop: 10 }}>
                  <TierLockedCard
                feature="Pricing guidance & marketplace recommendations"
                requiredTier="Seller"
                blurb="Suggested price ranges, fee-adjusted net by platform, and a full market report."
                  />
                </div>
              ) : (
            <Card style={{ padding: 14, marginTop: 10 }}>
              {(() => {
                const vr = scanStore.getVerifyResult();
                const pricing = vr?.pricing;
                const guidance = pricing?.guidance ?? null;
                const comparableCount = pricing?.comparable_count ?? 0;
                // F-D: the server now stamps WHY pricing is absent
                // (meta.pricing_status_note — e.g. "no comparable sold
                // listings", "verification ran long, re-scan for pricing").
                // Prefer that over the generic insufficient-data line so a
                // missing price is explained, never silently blank.
                const pricingStatusNote: string | null =
                  (vr as any)?.meta?.pricing_status_note ?? null;

                return (
                  <>
                    <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
                      Estimated Value
                    </div>
                    {guidance ? (
                      <>
                        <div style={{
                          fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
                          fontSize: 30, color: "hsl(var(--ink))", lineHeight: 1.05,
                          letterSpacing: "-0.02em",
                        }}>
                          {guidance.recommended_range}
                        </div>
                        {/* Market spread (raw min/max of comp prices) was
                            previously shown here but removed: outliers in
                            the comp set produced wildly misleading ranges
                            like $10–$210 next to a $16–$24 recommendation,
                            which read as "the algorithm ignored 90% of the
                            data." If reintroduced, trim to a tighter
                            percentile (P10–P90) first. */}
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                          color: "hsl(var(--ink-2))", marginTop: 10, letterSpacing: "0.06em",
                        }}>
                          Based on {comparableCount} comparable listing{comparableCount !== 1 ? "s" : ""}
                        </div>

                        {/* The "How was this calculated?" expandable was
                            removed — the source line, rationale, and Sample
                            comparables created more confusion than trust for
                            collectors who already see the recommended range
                            and comp count. Detail now lives on /price only. */}
                      </>
                    ) : (
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontSize: 14, color: "hsl(var(--ink-2))", lineHeight: 1.4,
                      }}>
                        {pricingStatusNote ??
                          "Insufficient market data — verify with current listings before pricing this item."}
                      </div>
                    )}
                  </>
                );
              })()}

              <button
                className="fig-block"
                onClick={() => nav("/price")}
                style={{
                  marginTop: 12, width: "100%",
                  border: "1px solid hsl(var(--ink))", background: "hsl(var(--ink))",
                  color: "hsl(var(--cream))", padding: "10px 12px", cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                  letterSpacing: "0.12em", textTransform: "uppercase",
                }}
              >
                Open full market report →
              </button>
            </Card>
              )
            )}
          </Section>
        )}
        </div>
        )}

        {/* ─── 6. WHAT'S NEXT — two clear actions ──────────────────────────
            Post → runs the new posting flow (carries the verified stamp via
            the saved scan row). Skip → keeps it private in the closet.   */}
        {/* ─── 5c. PREPARE MARKETPLACE LISTING — dropdown ──────── */}
        <Section pad={14}>
          <div style={{
            border: "1.5px solid hsl(var(--ink))",
            background: "hsl(var(--paper-3))",
            padding: "8px 14px 14px",
          }}>
            <div style={{ marginBottom: 10 }}>
              <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 2 }}>Next step</div>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 20, color: "hsl(var(--ink))", lineHeight: 1.1 }}>
                Prepare marketplace listing
              </div>
            </div>
            <div className="text-secondary-foreground" style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, lineHeight: 1.4, marginBottom: 10,
            }}>
              Pick a marketplace and Fig drafts the title, description, and tags using this verdict.
            </div>
            <PrepareListing item={item} verification={verification} savedScanId={savedScanId} />
          </div>
        </Section>

        {/* ─── 5d. PDF EXPORT (gated) ──────────────────────────── */}
        <Section pad={14}>
          <ScanPdfExportButton
            title={item.title || "FIG Verify scan"}
            photo={coverPhoto}
            notes={Array.isArray(item.notes) ? item.notes.join(" • ") : null}
            fields={[
              { label: "​", value: item.verdict },
              { label: "Brand", value: item.brand },
              { label: "Era", value: item.era },
              { label: "Category", value: item.category },
              { label: "Material", value: item.material },
              { label: "Size", value: item.size },
              { label: "Condition", value: item.condition },
              ...(features.canSeePricing
                ? [
                    { label: "Suggested price", value: `$${item.pricing.suggested}` },
                    { label: "Range", value: `$${item.pricing.low}–$${item.pricing.high}` },
                  ]
                : []),
            ]}
          />
        </Section>

        <Section pad={22} title={<span id="whats-next-heading" role="heading" aria-level={2}>What's next</span>}>
          <div role="region" aria-labelledby="whats-next-heading">
          {posted ? (
            <Card accent="hsl(var(--rust))" style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span aria-hidden="true" style={{ color: "hsl(var(--rust))" }}>{Icon.check(16)}</span>
                <span role="status" style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))" }}>
                  Live in your feed
                </span>
                <div style={{ flex: 1 }} />
                <ListingBadge
                  links={listingLinks}
                  isOwner
                  onEdit={() => setListingSheetOpen(true)}
                />
              </div>
            </Card>
          ) : (
            <ScanReviewActions
              scanId={savedScanId}
              postState={{
                photos,
                title: item.title,
                brand: item.brand,
                size: item.size,
                condition: item.condition,
                conditionNotes: item.conditionNotes,
                era: item.era,
                category: item.category,
                material: item.material,
                color: item.color,
                notes: item.notes ?? [],
                tags: item.tags ?? [],
                verification,
                verdict: item.verdict,
                verdictLabel: item.verdictLabel,
                confidence: item.confidence,
                priceSuggested: item.pricing.suggested,
              }}
              onActioned={(kind) => {
                actionedRef.current = true;
                if (kind === "closet" || kind === "discard") nav("/profile/@you");
              }}
            />
          )}
          </div>
        </Section>

        {/* ─── 7. FEEDBACK ─────────────────────────────────────── */}
        <Section pad={20} eyebrow="Help Fig improve">
          <VerdictFeedback
            scanId={savedScanId}
            onDisagreeNote={(note) => setDisagreeHint(note)}
          />
          {disagreeHint && savedScanId && (
            <button
              onClick={rerunWithHint}
              disabled={rerunning}
              style={{
                marginTop: 12, width: "100%", padding: "12px",
                background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: rerunning ? "wait" : "pointer", opacity: rerunning ? 0.6 : 1,
              }}
            >
              {rerunning ? "Re-running with your hint…" : "Re-run Fig with this hint"}
            </button>
          )}
        </Section>
      </div>
      <ListingStatusSheet
        open={listingSheetOpen}
        scanId={savedScanId}
        scanTitle={item.title}
        initialLinks={listingLinks}
        initialSold={soldInfo}
        onClose={() => setListingSheetOpen(false)}
        onSaved={({ links, posted: nextPosted, sold, deleted }) => {
          if (deleted) {
            scanStore.clearResult();
            sessionStorage.removeItem("fig_scan_saved_id");
            actionedRef.current = true;
            nav("/profile", { replace: true });
            return;
          }
          setListingLinks(links);
          setPosted(nextPosted);
          if (sold) setSoldInfo(sold);
        }}
      />
      {lightboxOpen && photos.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Photo gallery"
          onClick={() => setLightboxOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "hsla(0,0%,0%,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            ref={lightboxScrollerRef}
            className="no-scrollbar"
            onClick={(e) => e.stopPropagation()}
            onScroll={(e) => {
              const el = e.currentTarget;
              const idx = Math.round(el.scrollLeft / el.clientWidth);
              if (idx !== activePhoto) setActivePhoto(idx);
            }}
            style={{
              display: "flex", width: "100%", height: "100%",
              overflowX: "auto", overflowY: "hidden",
              scrollSnapType: "x mandatory",
              // Same fix as the other carousel in this file — allow vertical
              // drag to bubble to the page scroll container.
              touchAction: "pan-x pan-y",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {photos.map((url, i) => (
              <div
                key={url + i}
                style={{
                  flex: "0 0 100%", width: "100%", height: "100%",
                  scrollSnapAlign: "start",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 16, boxSizing: "border-box",
                }}
              >
                <img
                  src={url}
                  alt={`${item.title} ${i + 1}/${photos.length}`}
                  draggable={false}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", userSelect: "none" }}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            aria-label="Close gallery"
            onClick={() => setLightboxOpen(false)}
            style={{
              position: "absolute",
              // Offset by the iOS safe-area-inset-top so the X clears the
              // Dynamic Island / notch. The 16 baseline still applies on
              // devices with no inset (web, older iPhones).
              top: "calc(env(safe-area-inset-top, 0px) + 16px)",
              right: 16,
              width: 40, height: 40, borderRadius: "50%",
              border: "1px solid hsl(var(--cream))",
              background: "hsla(0,0%,0%,0.6)", color: "hsl(var(--cream))",
              fontSize: 20, lineHeight: 1, cursor: "pointer",
              zIndex: 1,
            }}
          >
            ×
          </button>

          {photos.length > 1 && (
            <>
              <div style={{
                position: "absolute",
                top: "calc(env(safe-area-inset-top, 0px) + 20px)",
                left: 20,
                background: "hsla(0,0%,0%,0.6)", color: "hsl(var(--cream))",
                padding: "4px 10px", fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.1em",
                border: "1px solid hsla(0,0%,100%,0.3)",
              }}>
                {activePhoto + 1} / {photos.length}
              </div>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={() => goToLightbox(activePhoto - 1)}
                disabled={activePhoto === 0}
                style={{
                  position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  border: "1px solid hsl(var(--cream))",
                  background: "hsla(0,0%,0%,0.6)", color: "hsl(var(--cream))",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: activePhoto === 0 ? "not-allowed" : "pointer",
                  opacity: activePhoto === 0 ? 0.4 : 1,
                }}
              >
                {Icon.chev(18, "l")}
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={() => goToLightbox(activePhoto + 1)}
                disabled={activePhoto === photos.length - 1}
                style={{
                  position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
                  width: 44, height: 44, borderRadius: "50%",
                  border: "1px solid hsl(var(--cream))",
                  background: "hsla(0,0%,0%,0.6)", color: "hsl(var(--cream))",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: activePhoto === photos.length - 1 ? "not-allowed" : "pointer",
                  opacity: activePhoto === photos.length - 1 ? 0.4 : 1,
                }}
              >
                {Icon.chev(18, "r")}
              </button>
            </>
          )}
        </div>
      )}
      {showDiscardConfirm && (
        <div
          role="dialog"
          aria-label="Discard verification"
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "rgba(20, 18, 14, 0.78)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
        >
          <div style={{
            width: "100%", maxWidth: 320,
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: 24,
            display: "flex", flexDirection: "column", gap: 16,
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22,
              color: "hsl(var(--ink))", lineHeight: 1.1,
            }}>
              Discard this verification?
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15,
              color: "hsl(var(--ink-2))", lineHeight: 1.4,
            }}>
              Your verification result will not be saved to your closet.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn"
                style={{ flex: 1, padding: 12 }}
                onClick={() => { setShowDiscardConfirm(false); }}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-rust"
                style={{ flex: 1, padding: 12 }}
                onClick={async () => {
                  const id = savedScanIdRef.current;
                  if (id) {
                    await supabase.from("scans").update({ review_status: "discarded", posted: false } as never).eq("id", id);
                    sessionStorage.removeItem("fig_scan_saved_id");
                    sessionStorage.removeItem("fig_current_scan_id");
                  }
                  actionedRef.current = true;
                  setShowDiscardConfirm(false);
                  nav("/scan");
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
      {savedScanId && item.pricing?.suggested ? (
        <PriceCorrectionDialog
          open={showPriceCorrection}
          onClose={() => setShowPriceCorrection(false)}
          scanId={savedScanId}
          currentPrice={item.pricing.suggested as number}
        />
      ) : null}
    </MobileFrame>
  );
}

// ─── Quick Thrift slim result view ─────────────────────────────────────────
// Single-screen mobile read for quick-mode scans. No forensic detail, no
// signals grid, no comp listings. Just the photo, the brand/era/type, the
// rough price range, the buy_signal label, and the upsell to full mode.
function QuickResultView({
  result,
  photos,
  item,
  onBack,
}: {
  result: FigVerifyResult;
  photos: string[];
  item: ReturnType<typeof mergedItem>;
  onBack: () => void;
}) {
  const r = result as unknown as Record<string, unknown>;
  const itemBlock = (r.item ?? {}) as Record<string, unknown>;
  const pricingBlock = (r.pricing ?? {}) as Record<string, unknown>;
  const brand = (itemBlock.brand_detected as string | null) ?? null;
  const era = (itemBlock.estimated_era as string | null) ?? null;
  const detectedType = (itemBlock.detected_type as string | null) ?? null;
  const buySignal = (r.buy_signal as string | null) ?? "uncertain";
  const oneLiner = (r.one_liner as string | null) ?? "";
  const priceLow = pricingBlock.rough_range_low as number | null;
  const priceHigh = pricingBlock.rough_range_high as number | null;

  const photo = photos[0] ?? item.img;

  const signalDisplay: Record<string, { label: string; bg: string; fg: string }> = {
    good_find: { label: "Good Find", bg: "hsl(var(--olive))", fg: "hsl(var(--cream))" },
    decent: { label: "Decent Pick", bg: "hsl(var(--ink))", fg: "hsl(var(--sand))" },
    skip: { label: "Skip", bg: "hsl(var(--rust))", fg: "hsl(var(--cream))" },
    uncertain: { label: "Uncertain", bg: "hsl(var(--paper-3))", fg: "hsl(var(--ink))" },
  };
  const sig = signalDisplay[buySignal] ?? signalDisplay.uncertain;

  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        {/* Top bar */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={onBack} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps" style={{ color: "hsl(var(--rust))", letterSpacing: "0.14em", fontSize: 11 }}>
            Quick Thrift Result
          </span>
          <div style={{ width: 30 }} />
        </div>

        {/* Photo */}
        {photo && (
          <div style={{ padding: "16px 16px 0" }}>
            <div className="fig-block" style={{
              width: "100%", aspectRatio: "4/3",
              border: "1px solid hsl(var(--ink))",
              overflow: "hidden", background: "hsl(var(--paper-3))",
            }}>
              <img src={photo} alt="Scanned item" style={{
                width: "100%", height: "100%", objectFit: "cover",
              }} />
            </div>
          </div>
        )}

        {/* Buy signal pill */}
        <div style={{ display: "flex", justifyContent: "center", padding: "16px 16px 4px" }}>
          <div style={{
            padding: "8px 22px", borderRadius: 999,
            background: sig.bg, color: sig.fg,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em",
            minWidth: 160, textAlign: "center",
          }}>
            {sig.label}
          </div>
        </div>

        {/* One-liner */}
        {oneLiner && (
          <div style={{
            padding: "8px 24px 16px", textAlign: "center",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 15, color: "hsl(var(--ink))", lineHeight: 1.35,
          }}>
            {oneLiner}
          </div>
        )}

        {/* 3-row summary */}
        <div style={{ padding: "0 16px" }}>
          <div style={{
            padding: "14px 16px",
            background: "hsl(var(--paper-2))",
            border: "1px solid hsl(var(--ink))",
            marginBottom: 12,
          }}>
            {[
              ["Brand", brand],
              ["Era", era],
              ["Type", detectedType],
            ].map(([label, value], i, arr) => (
              <div key={label as string} style={{ marginBottom: i === arr.length - 1 ? 0 : 12 }}>
                <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 4, fontSize: 9 }}>
                  {label}
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 14, color: "hsl(var(--ink))",
                }}>
                  {value || "—"}
                </div>
              </div>
            ))}
          </div>

          {/* Rough price range */}
          {priceLow != null && priceHigh != null && (
            <div style={{
              padding: "14px 16px",
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink))",
              marginBottom: 16,
            }}>
              <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
                Rough Price Range
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 800, fontSize: 28, color: "hsl(var(--ink))",
                lineHeight: 1.05, letterSpacing: "-0.02em",
              }}>
                ${priceLow}–${priceHigh}
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                color: "hsl(var(--ink-2))", marginTop: 8, letterSpacing: "0.06em",
              }}>
                Quick estimate · Full Verify uses real comp data
              </div>
            </div>
          )}

          {/* Upsell to Full Verify */}
          <button
            className="fig-block"
            onClick={() => nav("/scan")}
            style={{
              width: "100%", padding: "16px 22px",
              background: "hsl(var(--ink))",
              color: "hsl(var(--ink-fg))", border: "none",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, fontWeight: 600, letterSpacing: "0.14em",
              textTransform: "uppercase", cursor: "pointer",
            }}
          >
            Get Full Authentication →
          </button>

          <button
            onClick={() => nav("/scan/quick")}
            style={{
              width: "100%", marginTop: 10, padding: "12px 22px",
              background: "transparent",
              color: "hsl(var(--rust))",
              border: "1px dashed hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", cursor: "pointer",
            }}
          >
            Scan another
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}
